import React from 'react'
import { CheckinFeature, CheckinProject, Feature, ManualOverrideType } from '../../api/client'
import ReferenceValueDisplay from '../ReferenceValueDisplay'
import { boolLabelFor } from '../../utils/boolFeature'

type ProjectLite = { id: number; title: string; emoji?: string }

interface TombstoneDisclosureProps {
    tombstones: CheckinFeature[]
    projectTombstones: CheckinProject[]
    featuresById: Map<number, Feature>
    projectsById: Map<number, ProjectLite>
    collectionItems: Record<string, any>
    expanded: boolean
    onToggle: () => void
    onReset: (target: CheckinFeature) => void
    onResetProject: (projectId: number) => void
}

// Stable identity for a tombstone row, normalising undefined/null so the
// "drop one row" filter matches reliably regardless of how the backend
// serialised the unset fields.
export const tombstoneKey = (f: CheckinFeature): string =>
    `${f.feature_id}|${f.decimal_value ?? ''}|${f.freeform_value ?? ''}|${f.reference_value ?? ''}|${f.latitude ?? ''}|${f.longitude ?? ''}`

const renderLabel = (
    feature: Feature | undefined,
    fallbackId: number,
    projectsById: Map<number, ProjectLite>,
): React.ReactNode => {
    if (!feature) return <span>Feature #{fallbackId}</span>
    // Prefer the projects map (built from projects + projectDetails — same
    // sources the chip layout uses) since `feature.project` isn't reliably
    // hydrated on every Feature object that flows into the composer.
    if (feature.project_id) {
        const project = projectsById.get(feature.project_id) || feature.project
        if (project) {
            return (
                <span>
                    {project.emoji && <span className="mr-1">{project.emoji}</span>}
                    {project.title} · {feature.title}
                </span>
            )
        }
    }
    return <span>{feature.title}</span>
}

// A list/map DELETE row carrying no value at all is the feature-level
// "user cleared this" sentinel, not a removed entry. It has nothing to name,
// so it reads as one "cleared" line rather than a value.
const isClearedSentinel = (f: CheckinFeature): boolean =>
    f.manual_override === ManualOverrideType.DELETE &&
    f.decimal_value == null &&
    f.freeform_value == null &&
    f.reference_value == null &&
    f.latitude == null &&
    f.longitude == null &&
    f.key_freeform_value == null &&
    f.key_decimal_value == null &&
    f.key_reference_value == null

const renderValue = (
    feature: Feature | undefined,
    row: CheckinFeature,
    collectionItems: Record<string, any>,
): React.ReactNode => {
    const unknown = <em className="text-slate-400">(unknown value)</em>
    if (isClearedSentinel(row)) {
        return <em className="text-slate-400">cleared</em>
    }
    if (!feature) return unknown

    const featureType = feature.config?.type

    // No branch for list/map here on purpose. Under the feature-level lock
    // model their only DELETE shape is the value-less sentinel, which
    // `isClearedSentinel` answers above — a list tombstone naming a single
    // removed entry belongs to the model that preceded it and no longer
    // exists in the data. See `docs/list_tagging.md`.

    if (featureType === 'reference') {
        if (row.reference_value == null) return unknown
        const item = collectionItems[String(row.reference_value)]
        if (!item) return <em className="text-slate-400">(deleted item)</em>
        return (
            <ReferenceValueDisplay
                itemId={row.reference_value}
                showPrefix={false}
                collectionItems={collectionItems}
            />
        )
    }

    if (featureType === 'bool') {
        return boolLabelFor(row.decimal_value, feature.config?.params) ?? unknown
    }

    if (featureType === 'decimal' || featureType === 'rating' || featureType === 'labeled_rating') {
        if (row.decimal_value == null) return unknown
        if (featureType === 'labeled_rating') {
            const labels: string[] = feature.config?.params?.labels || []
            return labels[Math.floor(row.decimal_value) - 1] || String(row.decimal_value)
        }
        if (featureType === 'rating') {
            const max = feature.config?.params?.max || 5
            return `${row.decimal_value}/${max}`
        }
        return feature.unit ? `${row.decimal_value}${feature.unit}` : String(row.decimal_value)
    }

    // freeform / enum
    if (!row.freeform_value) return unknown
    return row.freeform_value
}

const TombstoneDisclosure: React.FC<TombstoneDisclosureProps> = ({
    tombstones,
    projectTombstones,
    featuresById,
    projectsById,
    collectionItems,
    expanded,
    onToggle,
    onReset,
    onResetProject,
}) => {
    const total = tombstones.length + projectTombstones.length
    if (total === 0) return null

    return (
        <div className="mt-4 border-t border-slate-200 pt-3">
            <button
                type="button"
                onClick={onToggle}
                className="flex items-center text-sm text-slate-500 hover:text-slate-700 focus:outline-none"
                aria-expanded={expanded}
            >
                <span>{total} {total === 1 ? 'tag' : 'tags'} you removed</span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform ${expanded ? 'rotate-180' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>
            {expanded && (
                <ul className="mt-2 space-y-1">
                    {projectTombstones.map((cp) => {
                        const project = projectsById.get(cp.project_id)
                        return (
                            <li
                                key={`project-${cp.project_id}`}
                                className="flex items-baseline gap-2 text-sm pl-3"
                            >
                                <div className="text-slate-400 truncate min-w-0">
                                    <span>· </span>
                                    {project ? (
                                        <span>
                                            {project.emoji && <span className="mr-1">{project.emoji}</span>}
                                            {project.title}
                                        </span>
                                    ) : (
                                        <span>Project #{cp.project_id}</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onResetProject(cp.project_id)}
                                    className="text-amber-800 hover:text-amber-900 text-sm flex-shrink-0"
                                >
                                    Reset
                                </button>
                            </li>
                        )
                    })}
                    {tombstones.map((row) => {
                        const feature = featuresById.get(row.feature_id)
                        return (
                            <li
                                key={tombstoneKey(row)}
                                className="flex items-baseline gap-2 text-sm pl-3"
                            >
                                <div className="text-slate-400 truncate min-w-0">
                                    <span>· </span>
                                    {renderLabel(feature, row.feature_id, projectsById)}
                                    <span>: </span>
                                    {renderValue(feature, row, collectionItems)}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onReset(row)}
                                    className="text-amber-800 hover:text-amber-900 text-sm flex-shrink-0"
                                >
                                    Reset
                                </button>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}

export default TombstoneDisclosure
